import { useEffect, useState } from 'react';
import {
  Combobox,
  V2FormField,
  V2FormGrid,
  V2FormActions,
  V2Button,
  Button,
  Alert,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { fetchNextCode } from '../../lib/suggestedCode.js';
import { MASTER_DATA_ENTITIES, type MasterDataEntityKind } from '../../lib/masterDataConfig.js';
import type { MasterDataListResponse, MasterDataRecord } from '../../lib/types/masterData.js';

/**
 * A named, searchable picker for another master-data entity's live
 * records — real `GET .../master-data/:entity?status=active` data through
 * the real `Combobox` primitive (already shipped in
 * `packages/design-system`, unused anywhere in this app until now), rather
 * than the raw foreign-key-ID text field this replaced.
 *
 * "empty → create doesn't dead-end": on a fresh tenant with nothing seeded
 * yet, the referenced entity's list is empty and there is no ID a user
 * could type even if the field stayed a text input. Rather than block the
 * parent form, this shows an inline "create" shortcut through the real
 * `POST .../master-data/:entity` endpoint (same code+name shape every
 * master-data entity's create accepts) and auto-selects the result.
 */
export function EntityPickerField({
  base,
  pickerEntity,
  value,
  onChange,
  required,
}: {
  readonly base: string;
  readonly pickerEntity: MasterDataEntityKind;
  readonly value: string;
  readonly onChange: (id: string) => void;
  readonly required?: boolean;
}) {
  const config = MASTER_DATA_ENTITIES.find((e) => e.kind === pickerEntity);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const list = useApiGet<MasterDataListResponse>(
    config ? `${base}/api/v1/master-data/${config.pathSegment}?status=active&limit=200` : null,
    [config?.pathSegment, refreshKey],
  );

  // The inline "create" form opens with the suggested code instead of an empty field.
  useEffect(() => {
    if (!creating || !pickerEntity) return;
    let cancelled = false;
    void fetchNextCode(base, pickerEntity).then((code) => {
      if (!cancelled && code) setNewCode((current) => current || code);
    });
    return () => {
      cancelled = true;
    };
  }, [creating, base, pickerEntity]);

  if (!config) return null;

  const options =
    list.status === 'ready'
      ? list.data.items.map((row) => {
          const id = String(row[config.idField]);
          const name = row.name ? String(row.name) : id;
          const code = row.code ? String(row.code) : undefined;
          return { value: id, label: code ? `${name} (${code})` : name };
        })
      : [];

  async function onCreateSubmit() {
    if (!newCode.trim() || !newName.trim()) return;
    setSubmitting(true);
    setCreateError(null);
    try {
      const created = await apiPost<MasterDataRecord>(
        `${base}/api/v1/master-data/${config!.pathSegment}`,
        { code: newCode.trim(), name: newName.trim() },
      );
      onChange(String(created[config!.idField]));
      setNewCode('');
      setNewName('');
      setCreating(false);
      setRefreshKey((k) => k + 1);
    } catch {
      setCreateError(`ثبت ${config!.label} ناموفق بود؛ ممکن است کد تکراری باشد.`);
    } finally {
      setSubmitting(false);
    }
  }

  const showEmptyPrompt = list.status === 'ready' && list.data.items.length === 0 && !creating;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Combobox
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder={`جستجوی ${config.label}`}
        required={required}
        disabled={list.status === 'loading'}
      />
      {list.status === 'error' ? (
        <Alert tone="danger" title="خطا">
          فهرست {config.label} قابل دریافت نیست.
        </Alert>
      ) : null}
      {showEmptyPrompt ? (
        <Alert tone="warning" title={`هیچ ${config.label} ثبت نشده`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span>برای ادامه، ابتدا یک {config.label} ثبت کنید.</span>
            <Button variant="secondary" size="sm" onClick={() => setCreating(true)}>
              {`ایجاد ${config.label} جدید`}
            </Button>
          </div>
        </Alert>
      ) : null}
      {creating ? (
        <div
          className="sk-card"
          style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <V2FormGrid>
            <V2FormField label="کد" required>
              <input
                className="sk-input"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                required
              />
            </V2FormField>
            <V2FormField label="نام" required>
              <input
                className="sk-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
              />
            </V2FormField>
          </V2FormGrid>
          {createError ? (
            <Alert tone="danger" title="خطا">
              {createError}
            </Alert>
          ) : null}
          <V2FormActions>
            <V2Button
              type="button"
              variant="primary"
              disabled={submitting}
              onClick={() => void onCreateSubmit()}
            >
              {submitting ? 'در حال ثبت…' : 'ثبت'}
            </V2Button>
            <V2Button type="button" variant="ghost" onClick={() => setCreating(false)}>
              انصراف
            </V2Button>
          </V2FormActions>
        </div>
      ) : null}
    </div>
  );
}
